import { Box } from '@mantine/core';
import { useMemo } from 'react';

import { TransitStatus } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { extractStatus } from '../../utils';
import classes from './TransitStatusIndicator.module.css';

interface TransitStatusIndicatorProps {
  status: TransitStatus;
  isDeparture?: boolean;
  releaseIndicator?: string;
}

export const TransitStatusIndicator: React.FC<TransitStatusIndicatorProps> = ({ status, isDeparture, releaseIndicator }) => {
  const getReleaseIcon = () => {
    if (isDeparture) {
      return IconFactory.lightGreenSuccess;
    }

    const isPartialRelease = ['2', '3', '4'].includes(releaseIndicator);
    if (isPartialRelease) {
      return IconFactory.rejected;
    }

    return IconFactory.released;
  };

  const statusIconMapper = useMemo(
    () =>
      new Map([
        [TransitStatus.NOT_SENT, { icon: IconFactory.notSent, alt: 'NOT_SENT' }],
        [TransitStatus.SENDING, { icon: IconFactory.processing, alt: 'SENDING' }],
        [TransitStatus.SENT, { icon: IconFactory.processing, alt: 'SENT' }],
        [TransitStatus.ACCEPTED, { icon: IconFactory.processing, alt: 'ACCEPTED' }],
        [TransitStatus.REJECTED, { icon: IconFactory.rejected, alt: 'REJECTED' }],
        [TransitStatus.IN_CONTROL, { icon: IconFactory.anomalyValue, alt: 'IN_CONTROL' }],
        [TransitStatus.CORR_REFUSED_WAITING, { icon: IconFactory.processing, alt: 'CORR_REFUSED_WAITING' }],
        [TransitStatus.NOT_RELEASED, { icon: IconFactory.notReleased, alt: 'NOT_RELEASED' }],
        [TransitStatus.CORR_OK_WAITING, { icon: IconFactory.processing, alt: 'CORR_OK_WAITING' }],
        [TransitStatus.RELEASED, { icon: getReleaseIcon(), alt: 'RELEASED' }],
        [TransitStatus.FINISHED, { icon: IconFactory.released, alt: 'FINISHED' }],
        [TransitStatus.INVALIDATED, { icon: IconFactory.invalidated, alt: 'INVALIDATED' }],
        [TransitStatus.REMARKS_SENT, { icon: IconFactory.processing, alt: 'REMARKS_SENT' }],
        [TransitStatus.UNLOAD_PERMISSION, { icon: IconFactory.lightGreenSuccess, alt: 'UNLOAD_PERMISSION' }],
        [TransitStatus.GUARANTEE_NOT_VALID, { icon: IconFactory.rejected, alt: 'GUARANTEE_NOT_VALID' }],
        [TransitStatus.DISCREPANCIES, { icon: IconFactory.greenRejected, alt: 'DISCREPANCIES' }],
        [TransitStatus.REQUESTED_NON_ARRIVED, { icon: IconFactory.anomalyValue, alt: 'REQUESTED_NON_ARRIVED' }],
        [TransitStatus.INCIDENT, { icon: IconFactory.rejected, alt: 'INCIDENT' }],
        [TransitStatus.RECOVERY, { icon: IconFactory.anomalyValue, alt: 'RECOVERY' }],
      ]),
    [isDeparture, releaseIndicator],
  );
  const currentStatus = statusIconMapper.get(status);

  const currentDescription = extractStatus(status);

  if (!currentStatus) {
    return null;
  }

  return (
    <Box display={'flex'}>
      <Box className={classes.svgIcon} title={currentDescription}>
        <img src={currentStatus.icon} alt={currentStatus.alt} />
      </Box>
    </Box>
  );
};
